import { type PowerSyncDrizzleDB, user } from "@vigia/powersync";
import { patchRowVerified, type VerifiedWriteOptions } from "../powersync/verified-write";

/**
 * ── §4.11: `user` is NOT excluded from verified write ───────────────────────
 * (updated after the `ThemeProvider` seed guard — see below)
 *
 * Campaign §4.11 excludes `user` in apps where the user row already exists
 * beforehand (login brings it synced). That premise does NOT hold here, and
 * it still does not hold even though `ThemeProvider` is no longer part of the problem:
 *
 *   - `db` (PowerSync accessor) becomes non-null as soon as the local
 *     SQLite opens, not when the initial sync finishes.
 *   - `userId()` activates as soon as the server confirms the session (a
 *     REST call independent of the PowerSync stream) — it does not wait for the
 *     checkpoint.
 *
 * Between `db`+`userId()` becoming ready and the initial checkpoint bringing the
 * `user` row created by the server (`packages/core-rust/src/db/user.rs:101,
 * 350` — unconditional `INSERT INTO "user"` on every sign-up/OAuth), there is a
 * window in which a normal `UPDATE` (changing language, theme…) falls against a
 * row that the local view does not have yet — it matches ZERO rows and returns
 * "success" silently. That is why the six mutations below go through
 * `patchRowVerified`.
 *
 * (Historical context, already CLOSED) Until the boot guard, a THIRD
 * factor worsened this same window for real accounts: `ThemeProvider`'s
 * `createEffect` (`apps/web/src/providers/theme-provider.tsx`) inserted
 * locally a "phantom" 3-column `user` row for ANY `uid`,
 * without distinguishing guest from real account, competing with the real row that the
 * server had already created and that was on its way by sync. The guard
 * `uid === VISITOR_ID` in that `createEffect` closed that specific root cause
 * (measured in `packages/shared/src/users/__tests__/theme-provider-seed-window.test.ts`).
 * **That does NOT make the `patchRowVerified` protection over `user` unnecessary**
 * — the two points above (`db` and `userId()` ready before the
 * checkpoint) still open the same window for any early write,
 * with or without a phantom row involved. Nor does it touch the per-organization
 * `scope` of the five fleet mutations (`fleet/mutations.ts`:
 * `enrollDeviceMutation`, `resolveAlertMutation`, `archiveDeviceMutation`,
 * `decideConsentMutation`, `endRemoteControlMutation`) nor the `isNull()` that
 * `verified-write.ts` needs so that a NULL `organization_id` (personal
 * mode) is compared as `IS NULL` instead of `= NULL` — that mechanism is
 * independent of `ThemeProvider` and remains intact. If `patchRowVerified`
 * is ever removed from `user`, may it be because it is proven that the window
 * above no longer exists, not because `ThemeProvider` stopped seeding phantoms.
 *
 * ── Why ALL the mutations below carry `.where(eq(user.id, userId))` ─────────
 *
 * Four of them (`locale`, `global_theme_id`, `is_blur`,
 * `follow_device_theme`) had this shape:
 *
 *     const query = db.update(user).set({ … });
 *     await (userId === VISITOR_ID ? query : query.where(eq(user.id, userId)));
 *
 * that is, in guest mode the UPDATE came out **without `WHERE`** → it rewrote ALL
 * the `user` rows of the local database. The branch existed by symmetry with
 * `getUserThemeSettingsQuery`, which for the guest reads with `.limit(1)` without a
 * filter; but a read `limit(1)` and an UPDATE without a filter are not the same
 * bet: the first picks the wrong row, the second crushes them all.
 *
 * The premise it hung on ("the guest database has only one `user`
 * row") is false in a specific window: on the guest→real upgrade the
 * guest database CONNECTS with the real account's connector and
 * auto-subscribes to all the streams — including `organizations`, which syncs
 * one `user` row for EACH co-member of that account's organizations.
 * There the table has 1 + 1 + N rows, `user` is NOT `localOnly`, and each row
 * touched enqueues its own op in `ps_crud`.
 *
 * The correct filter is NOT guessed: the guest row is seeded by
 * `ThemeProvider` (`apps/web/src/providers/theme-provider.tsx`) with
 * `db.insert(user).values({ id: uid, … })`, and for a guest tab `uid`
 * is exactly `VISITOR_ID`. So `eq(user.id, userId)` points at the SAME
 * row that the filterless branch intended to touch when the table had a single
 * row — identical behavior in the normal case, bounded in the rest. It is
 * also the shape that `updateUserFontFamilyMutation` and
 * `updateUserImageMutation` already used, which never had a guest branch.
 *
 * Guarded by `packages/shared/src/users/__tests__/mutations-where.test.ts`.
 */

/**
 * Shared Drizzle mutation for updating User locale.
 */
export async function updateUserLocaleMutation(
  db: PowerSyncDrizzleDB,
  userId: string,
  locale: string,
  options: VerifiedWriteOptions = {},
): Promise<void> {
  await patchRowVerified(db, user, user.id, userId, { locale }, options);
}

/**
 * Shared Drizzle mutation for updating User font family preference.
 */
export async function updateUserFontFamilyMutation(
  db: PowerSyncDrizzleDB,
  userId: string,
  fontFamilyId: string | null,
  options: VerifiedWriteOptions = {},
): Promise<void> {
  await patchRowVerified(db, user, user.id, userId, { font_family: fontFamilyId }, options);
}

/**
 * Writes the avatar KEY into the local row.
 *
 * It is written local-first (like language, font and theme) and not over REST: that way
 * the new photo is seen instantly and survives a cold reload without
 * connection. The BINARY does go over REST (`POST /api/avatar`) because it does not fit in the
 * sync queue; what travels through here is only the key that upload returns.
 *
 * `null` = "remove the photo" (the initials come back).
 *
 * The guest has no own syncable row and cannot have an avatar (rule
 * zero), so there is no `VISITOR_ID` branch here as in `locale`/`theme`.
 */
export async function updateUserImageMutation(
  db: PowerSyncDrizzleDB,
  userId: string,
  imageKey: string | null,
  options: VerifiedWriteOptions = {},
): Promise<void> {
  await patchRowVerified(db, user, user.id, userId, { image: imageKey }, options);
}

/**
 * Shared Drizzle mutation for updating global theme.
 */
export async function updateGlobalThemeMutation(
  db: PowerSyncDrizzleDB,
  userId: string,
  themeId: string | null,
  options: VerifiedWriteOptions = {},
): Promise<void> {
  await patchRowVerified(db, user, user.id, userId, { global_theme_id: themeId }, options);
}

/**
 * Shared Drizzle mutation for updating blur effect preference.
 */
export async function updateBlurMutation(
  db: PowerSyncDrizzleDB,
  userId: string,
  isBlur: boolean,
  options: VerifiedWriteOptions = {},
): Promise<void> {
  await patchRowVerified(db, user, user.id, userId, { is_blur: isBlur ? 1 : 0 }, options);
}

/**
 * Shared Drizzle mutation for updating follow device appearance preference.
 */
export async function updateFollowDeviceThemeMutation(
  db: PowerSyncDrizzleDB,
  userId: string,
  followDevice: boolean,
  options: VerifiedWriteOptions = {},
): Promise<void> {
  await patchRowVerified(
    db,
    user,
    user.id,
    userId,
    { follow_device_theme: followDevice ? 1 : 0 },
    options,
  );
}
